import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Search, Video, ShieldCheck, ArrowRight, Check, Award, BadgeCheck,
} from 'lucide-react';

const STAGES = [
  { label: 'Still Shopping', price: '$19 Toolkit' },
  { label: 'Found a Vehicle', price: '$49 First Look' },
  { label: 'Ready to Buy', price: '$199 Inspection' },
];

const TOOLKIT_FEATURES = [
  'Listing Screening Guide',
  'Seller Questions',
  'Dealer & Private Seller Red Flags',
  'Vehicle History Guidance',
  'Marketplace Scam Guide',
  'Vehicle Checklists',
  'Test Drive Checklist',
  'Negotiation Worksheet',
  'Buyer Decision Scorecard',
  'Mobile-Friendly Instant Access',
];

const FIRSTLOOK_FEATURES = [
  'Live 1-on-1 Consultation',
  'Listing Review',
  'Pricing Review',
  'Available History Review',
  'Photo Review',
  'Potential Red Flags',
  'Seller Questions',
  'Recommended Next Step',
];

const INSPECTION_FEATURES = [
  'Comprehensive Vehicle Inspection',
  'OBD-II Diagnostic Scan',
  'Exterior & Interior Evaluation',
  'Engine Bay Inspection',
  'Tires, Brakes & Suspension',
  'Road Test When Permitted',
  'Detailed Vehicle Photos',
  'Vehicle Decision Report\u2122',
];

const FeatureList = ({ items, accent, testid }) => (
  <ul className="space-y-2.5 text-left" data-testid={testid}>
    {items.map((f, i) => (
      <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-300">
        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${accent}`} strokeWidth={3} />
        <span>{f}</span>
      </li>
    ))}
  </ul>
);

export const BuyerJourney = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-20 md:py-28" data-testid="buyer-journey-section" id="buyer-journey">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <BadgeCheck className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">Your Car-Buying Journey</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Where Are You in Your Car-Buying Journey?
          </h2>
          <p className="mt-5 text-zinc-300 text-base md:text-lg leading-relaxed">
            From searching for the right vehicle to making the final decision, CarLens gives you the right level of support at the right time.
          </p>
        </div>

        {/* Journey progression strip */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4" data-testid="journey-progression">
          {STAGES.map((s, i) => (
            <div key={s.label} className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <div className="rounded-full border border-zinc-700 bg-[#161618] px-5 py-2.5 text-center">
                <span className="block text-[11px] uppercase tracking-widest text-orange-400 font-semibold">{s.label}</span>
                <span className="block text-sm text-zinc-300 font-medium">{s.price}</span>
              </div>
              {i < STAGES.length - 1 && (
                <ArrowRight className="w-5 h-5 text-zinc-600 rotate-90 sm:rotate-0 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
        <p className="text-center text-zinc-500 text-sm max-w-2xl mx-auto mb-14">
          As you get closer to buying, CarLens can give you a deeper level of vehicle-specific support.
        </p>

        {/* Cards — mobile order: Inspection, First Look, Toolkit; desktop chronological */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">

          {/* CARD 3 — READY TO BUY (dominant) */}
          <div
            className="order-1 lg:order-3 relative flex flex-col rounded-3xl border-2 border-orange-500 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 shadow-2xl shadow-orange-500/30 overflow-hidden lg:-mt-4 lg:mb-4"
            data-testid="journey-card-inspection"
          >
            <div className="absolute -top-24 -right-24 w-56 h-56 bg-orange-500/20 rounded-full blur-3xl" />
            <div className="relative flex flex-col flex-1">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-2xl bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-orange-400" />
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white px-3 py-1 rounded-full shadow-lg shadow-orange-500/40">
                  <Award className="w-3 h-3" /> Most Complete
                </span>
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-orange-400 mb-2">Ready to Buy?</span>
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>CarLens Pre-Purchase Inspection</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-orange-500 align-middle">$199</span>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed mb-6">
                Before you commit, let CarLens physically inspect the vehicle. Our independent pre-purchase car inspection evaluates the actual vehicle so you can make your buying decision with better information.
              </p>
              <FeatureList items={INSPECTION_FEATURES} accent="text-orange-400" testid="journey-inspection-features" />
              <div className="mt-auto pt-7">
                <p className="text-center text-zinc-500 text-xs mb-3">Choose your vehicle type at booking &middot; cars, trucks, SUVs & exotics</p>
                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-base font-bold rounded-xl shadow-lg shadow-orange-500/30"
                  onClick={() => navigate('/book')}
                  data-testid="journey-inspection-cta"
                >
                  Book an Inspection &mdash; $199
                </Button>
              </div>
            </div>
          </div>

          {/* CARD 2 — FOUND A VEHICLE */}
          <div
            className="order-2 lg:order-2 flex flex-col rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 hover:border-orange-500/40 transition-colors"
            data-testid="journey-card-firstlook"
          >
            <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-5">
              <Video className="w-6 h-6 text-orange-400" />
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-orange-400 mb-2">Found a Vehicle?</span>
            <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>CarLens First Look&trade;</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-white align-middle">$49</span>
              <span className="ml-2 text-xs text-zinc-500 uppercase tracking-wide">Live Consultation</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              Found a vehicle but aren&rsquo;t sure whether it&rsquo;s worth pursuing? Meet live with a CarLens expert in a car buying consultation to review the listing, asking price, available history, photos, seller info and potential red flags before deciding what to do next.
            </p>
            <div className="mb-6 rounded-xl border border-orange-500/25 bg-orange-500/5 px-4 py-2.5">
              <p className="text-xs text-orange-300 leading-relaxed">
                <strong>Local to Northeast Florida?</strong> Your $49 is credited toward a full CarLens Inspection of that vehicle if you proceed.
              </p>
            </div>
            <FeatureList items={FIRSTLOOK_FEATURES} accent="text-orange-400/80" testid="journey-firstlook-features" />
            <div className="mt-auto pt-7">
              <p className="text-center text-zinc-500 text-xs mb-3">
                Ready to buy this vehicle?{' '}
                <button onClick={() => navigate('/book')} className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-2" data-testid="journey-firstlook-to-inspection">
                  Book a Pre-Purchase Inspection instead
                </button>.
              </p>
              <Button
                size="lg"
                variant="outline"
                className="w-full border-orange-500/50 text-orange-300 hover:bg-orange-500/10 py-6 text-base font-semibold rounded-xl"
                onClick={() => navigate('/firstlook')}
                data-testid="journey-firstlook-cta"
              >
                Book First Look &mdash; $49
              </Button>
            </div>
          </div>

          {/* CARD 1 — STILL SHOPPING */}
          <div
            className="order-3 lg:order-1 flex flex-col rounded-3xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8 hover:border-orange-500/40 transition-colors"
            data-testid="journey-card-toolkit"
          >
            <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-5">
              <Search className="w-6 h-6 text-orange-400" />
            </div>
            <span className="text-xs font-bold uppercase tracking-widest text-orange-400 mb-2">Still Shopping?</span>
            <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Ultimate Used Car Buyer Toolkit&trade;</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-white align-middle">$19</span>
              <span className="ml-2 text-xs text-zinc-500 uppercase tracking-wide">One-Time &middot; Instant Access</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed mb-6">
              Still searching for the right vehicle? Use the CarLens Ultimate Used Car Buyer Toolkit&trade; as your used car buyer guide to screen listings, ask better questions, recognize common warning signs and narrow your search to vehicles worth pursuing.
            </p>
            <FeatureList items={TOOLKIT_FEATURES} accent="text-orange-400/80" testid="journey-toolkit-features" />
            <div className="mt-auto pt-7">
              <p className="text-center text-zinc-500 text-xs mb-3">
                Already found the vehicle you want to buy? Skip the toolkit and book a{' '}
                <button onClick={() => navigate('/book')} className="text-orange-400 hover:text-orange-300 font-medium underline underline-offset-2" data-testid="journey-toolkit-to-inspection">
                  CarLens Pre-Purchase Inspection
                </button>.
              </p>
              <Button
                size="lg"
                variant="outline"
                className="w-full border-zinc-700 text-zinc-200 hover:bg-white/5 hover:text-white py-6 text-base font-semibold rounded-xl"
                onClick={() => navigate('/used-car-buyer-toolkit')}
                data-testid="journey-toolkit-cta"
              >
                Get the Toolkit &mdash; $19
              </Button>
            </div>
          </div>
        </div>

        {/* Final CTA — steer high intent to the inspection */}
        <div className="mt-16 rounded-3xl border-2 border-orange-500/50 bg-gradient-to-r from-orange-500/10 to-orange-600/10 p-6 sm:p-10 md:p-14 text-center relative overflow-hidden" data-testid="journey-final-cta">
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-orange-500/15 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
              Found the Vehicle You Want?
            </h2>
            <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
              Don&rsquo;t make the final decision based on the listing alone. Let CarLens inspect the actual vehicle before you commit.
            </p>
            <Button
              size="lg"
              className="w-full sm:w-auto whitespace-normal h-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-lg shadow-orange-500/30"
              onClick={() => navigate('/book')}
              data-testid="journey-final-inspection-cta"
            >
              Book a Pre-Purchase Inspection &mdash; $199
            </Button>
            <p className="text-zinc-400 text-sm mt-6">
              Still searching? Start with the{' '}
              <Link to="/used-car-buyer-toolkit" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="journey-final-toolkit-link">
                CarLens Ultimate Used Car Buyer Toolkit&trade;
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuyerJourney;
